// Citizen Charter PDF engine — client-side, template-driven.
// The document structure comes from the ACTIVE studio template (ordered page
// list); colours come from the charter's own palette snapshot. Dynamic pages are
// rendered with pdf-lib + fontkit (proper OpenType shaping for Malayalam
// conjuncts/chillus; fonts subset-embedded). Official fixed PDF page sets are
// merged untouched via copyPages — never redrawn, recoloured or rasterized.
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { fetchFontTtf } from "@/lib/charterFonts";
import { hexToRgbArr } from "@/lib/colorUtils";

// fallback palette (matches the studio default) if a template/charter lacks one
export const CC_DEFAULT_PALETTE = {
  primary: "#166B3F", secondary: "#0C4A2C", accent: "#B8912F",
  heading: "#7A2E1D", background: "#FBF7EE", table_header: "#166B3F",
  highlight: "#F4EFE3", text: "#1F2937", muted: "#6B7280", border: "#E3D5B8",
};

const C = (arr) => rgb(arr[0] / 255, arr[1] / 255, arr[2] / 255);

function buildPal(palette) {
  const p = { ...CC_DEFAULT_PALETTE, ...(palette || {}) };
  const out = {};
  for (const k of Object.keys(CC_DEFAULT_PALETTE)) out[k] = hexToRgbArr(p[k]);
  out.white = [255, 255, 255];
  return out;
}

const PAGE_W = 595.28, PAGE_H = 841.89; // A4 portrait
const MARGIN = 50;
const CONTENT_W = PAGE_W - 2 * MARGIN;

const isMl = (ch) => {
  const c = ch.codePointAt(0);
  return (c >= 0x0d00 && c <= 0x0d7f) || (c >= 0x200c && c <= 0x200d);
};

function splitRuns(text) {
  const runs = [];
  let cur = "", curMl = null;
  for (const ch of String(text ?? "")) {
    const m = isMl(ch);
    if (curMl === null || m === curMl) { cur += ch; curMl = m; }
    else { runs.push({ t: cur, ml: curMl }); cur = ch; curMl = m; }
  }
  if (cur) runs.push({ t: cur, ml: !!curMl });
  return runs;
}

function widthOf(text, size, fonts, bold) {
  let w = 0;
  for (const r of splitRuns(text)) {
    const f = r.ml ? (bold ? fonts.mlBold : fonts.ml) : (bold ? fonts.latinBold : fonts.latin);
    w += f.widthOfTextAtSize(r.t, size);
  }
  return w;
}

function wrapText(text, maxWidth, size, fonts, bold) {
  const out = [];
  for (const para of String(text ?? "").split("\n")) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(""); continue; }
    let line = "";
    for (const w of words) {
      const trial = line ? line + " " + w : w;
      if (widthOf(trial, size, fonts, bold) <= maxWidth || !line) line = trial;
      else { out.push(line); line = w; }
    }
    out.push(line);
  }
  return out;
}

function drawRuns(page, text, { x, y, size, fonts, bold = false, color, align = "left", maxWidth }) {
  const total = widthOf(text, size, fonts, bold);
  let cx = x;
  if (align === "center") cx = x + ((maxWidth ?? 0) - total) / 2;
  else if (align === "right") cx = x + (maxWidth ?? 0) - total;
  let pen = cx;
  for (const r of splitRuns(text)) {
    const f = r.ml ? (bold ? fonts.mlBold : fonts.ml) : (bold ? fonts.latinBold : fonts.latin);
    page.drawText(r.t, { x: pen, y, size, font: f, color: C(color) });
    pen += f.widthOfTextAtSize(r.t, size);
  }
  return total;
}

function drawParagraph(page, text, { x, y, size = 11, fonts, bold = false, color, align = "left", maxWidth, lineHeight = 1.45 }) {
  const lines = wrapText(text, maxWidth, size, fonts, bold);
  let cy = y;
  for (const ln of lines) {
    drawRuns(page, ln, { x, y: cy, size, fonts, bold, color, align, maxWidth });
    cy -= size * lineHeight;
  }
  return lines.length * size * lineHeight;
}

async function embedImg(doc, dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) return null;
  try {
    if (dataUrl.startsWith("data:image/png")) return await doc.embedPng(dataUrl);
    return await doc.embedJpg(dataUrl);
  } catch { return null; }
}

// fontkit is Node-oriented and breaks CRA/webpack5 — use the prebuilt browser
// vendor bundle (scripts/build-fontkit-vendor.js), lazy-loaded so it stays out
// of the main app chunk. The bundle also exports its own Buffer polyfill; font
// bytes MUST be wrapped with that exact Buffer (fontkit's probe does
// buffer.toString('ascii', 0, 4), which plain Uint8Array lacks).
let _vendor = null;
async function loadVendor() {
  if (!_vendor) {
    const mod = await import("@/vendor/fontkit");
    _vendor = mod.default || mod;
  }
  return _vendor;
}

async function buildFonts(doc, family, vendor) {
  const { Buffer } = vendor;
  let ml = null, mlBold = null;
  try { ml = await doc.embedFont(Buffer.from(await fetchFontTtf(family, 400)), { subset: true }); } catch (e) { console.error("Charter font embed failed (400):", e); }
  try { mlBold = await doc.embedFont(Buffer.from(await fetchFontTtf(family, 700)), { subset: true }); } catch (e) { console.error("Charter font embed failed (700):", e); mlBold = ml; }
  // Malayalam headings are mandatory on every page — without the font the output
  // would be broken anyway, so fail fast with a clear, actionable message.
  if (!ml) throw new Error("Malayalam font could not be loaded — please check your internet connection and try again");
  if (!mlBold) mlBold = ml;
  return {
    ml, mlBold,
    latin: await doc.embedFont(StandardFonts.Helvetica),
    latinBold: await doc.embedFont(StandardFonts.HelveticaBold),
  };
}

/* ------------------------------ page furniture ------------------------------ */
const pageTitle = (pageDef) =>
  [pageDef.title_ml, pageDef.title_en].filter(Boolean).join(" · ");

function pageHeader(page, fonts, pal, lbName, title, year) {
  page.drawRectangle({ x: 0, y: PAGE_H - 34, width: PAGE_W, height: 34, color: C(pal.background) });
  page.drawRectangle({ x: 0, y: PAGE_H - 37, width: PAGE_W, height: 3, color: C(pal.accent) });
  drawRuns(page, `പൗരാവകാശ രേഖ ${year}`, { x: MARGIN, y: PAGE_H - 24, size: 11, fonts, bold: true, color: pal.primary });
  if (lbName) {
    drawRuns(page, lbName, { x: PAGE_W - MARGIN, y: PAGE_H - 24, size: 9, fonts, color: pal.muted, align: "right", maxWidth: 0 });
  }
  if (title) {
    drawRuns(page, title, { x: MARGIN, y: PAGE_H - 62, size: 16, fonts, bold: true, color: pal.heading, maxWidth: CONTENT_W });
    page.drawRectangle({ x: MARGIN, y: PAGE_H - 70, width: 60, height: 2.5, color: C(pal.accent) });
  }
}

function pageFooter(page, fonts, pal, idx, total, lbName) {
  page.drawRectangle({ x: MARGIN, y: 34, width: CONTENT_W, height: 0.8, color: C(pal.border) });
  drawRuns(page, lbName || "", { x: MARGIN, y: 22, size: 8, fonts, color: pal.muted });
  drawRuns(page, `Page ${idx} of ${total}`, { x: PAGE_W - MARGIN, y: 22, size: 8, fonts, color: pal.muted, align: "right", maxWidth: 0 });
}

/* ------------------------------ section renderers ------------------------------ */
const fieldsOf = (pageDef) => (pageDef.config?.fields || []).filter(f => f.enabled !== false);
const fieldVal = (pageDef, data, key) => (data || {})[key];
const hasField = (pageDef, key) => fieldsOf(pageDef).some(f => f.key === key);

function renderCover(doc, fonts, pal, charter, pageDef, year) {
  const s = charter.sections[pageDef.section_key] || {};
  const lb = charter.local_body || {};
  const page = doc.addPage([PAGE_W, PAGE_H]);
  page.__cc_plain = true;
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: PAGE_H, color: C(pal.background) });
  page.drawRectangle({ x: 0, y: PAGE_H - 14, width: PAGE_W, height: 14, color: C(pal.primary) });
  page.drawRectangle({ x: 0, y: PAGE_H - 18, width: PAGE_W, height: 4, color: C(pal.accent) });
  let y = PAGE_H - 150;
  drawRuns(page, "തദ്ദേശ സ്വയംഭരണ വകുപ്പ്", { x: MARGIN, y, size: 13, fonts, color: pal.muted, align: "center", maxWidth: CONTENT_W });
  y -= 26;
  drawRuns(page, "LOCAL SELF GOVERNMENT DEPARTMENT", { x: MARGIN, y, size: 9, fonts, color: pal.muted, align: "center", maxWidth: CONTENT_W });
  y -= 90;
  const lbName = lb.local_body_name || "";
  drawRuns(page, lbName, { x: MARGIN, y, size: 26, fonts, bold: true, color: pal.primary, align: "center", maxWidth: CONTENT_W });
  y -= 22;
  const lbLine = [lb.local_body_type, lb.district ? `${lb.district} ജില്ല` : ""].filter(Boolean).join(" · ");
  drawRuns(page, lbLine, { x: MARGIN, y, size: 12, fonts, color: pal.secondary, align: "center", maxWidth: CONTENT_W });
  y -= 100;
  drawRuns(page, fieldVal(pageDef, s, "title_ml") || pageDef.title_ml || "പൗരാവകാശ രേഖ", { x: MARGIN, y, size: 40, fonts, bold: true, color: pal.heading, align: "center", maxWidth: CONTENT_W });
  y -= 60;
  const yearTxt = fieldVal(pageDef, s, "year") || String(year);
  const bandW = 150, bandH = 34;
  page.drawRectangle({ x: (PAGE_W - bandW) / 2, y, width: bandW, height: bandH, color: C(pal.secondary) });
  drawRuns(page, yearTxt, { x: (PAGE_W - bandW) / 2, y: y + 10, size: 18, fonts, bold: true, color: pal.white, align: "center", maxWidth: bandW });
  y -= 30;
  drawRuns(page, fieldVal(pageDef, s, "subtitle_en") || "CITIZEN CHARTER", { x: MARGIN, y, size: 11, fonts, color: pal.accent, align: "center", maxWidth: CONTENT_W });
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: 56, color: C(pal.secondary) });
  drawRuns(page, lbName, { x: MARGIN, y: 32, size: 13, fonts, bold: true, color: pal.white, align: "center", maxWidth: CONTENT_W });
  drawRuns(page, `${lb.district || ""} · Kerala`, { x: MARGIN, y: 14, size: 9, fonts, color: pal.white, align: "center", maxWidth: CONTENT_W });
}

async function renderNote(doc, fonts, pal, charter, pageDef, year) {
  const p = charter.sections[pageDef.section_key] || {};
  const lb = charter.local_body || {};
  const page = doc.addPage([PAGE_W, PAGE_H]);
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: PAGE_H, color: C(pal.white) });
  pageHeader(page, fonts, pal, lb.local_body_name, pageTitle(pageDef), year);
  let img = null;
  if (hasField(pageDef, "photo")) {
    img = await embedImg(doc, p.photo);
    if (img) {
      const fw = 110, fh = 130, fx = PAGE_W - MARGIN - fw, fy = PAGE_H - 220;
      page.drawRectangle({ x: fx - 4, y: fy - 4, width: fw + 8, height: fh + 8, borderColor: C(pal.accent), borderWidth: 1.5, color: C(pal.white) });
      page.drawImage(img, { x: fx, y: fy, width: fw, height: fh });
    }
  }
  drawRuns(page, "പൗരാവകാശ രേഖയ്ക്ക്", { x: MARGIN, y: PAGE_H - 120, size: 15, fonts, bold: true, color: pal.heading, maxWidth: CONTENT_W });
  drawRuns(page, "ഒര മുഖക്കുറിപ്പ്", { x: MARGIN, y: PAGE_H - 142, size: 13, fonts, color: pal.heading, maxWidth: CONTENT_W });
  let y = PAGE_H - 200;
  if (hasField(pageDef, "message")) {
    const used = drawParagraph(page, p.message || "—", { x: MARGIN, y, size: 11.5, fonts, color: pal.text, maxWidth: CONTENT_W - (img ? 130 : 0), lineHeight: 1.6 });
    y -= used + 30;
  }
  if (hasField(pageDef, "signature")) {
    const sigImg = await embedImg(doc, p.signature);
    if (sigImg) page.drawImage(sigImg, { x: PAGE_W - MARGIN - 120, y: y - 10, width: 120, height: 40 });
  }
  drawRuns(page, new Date().toLocaleDateString("en-GB"), { x: MARGIN, y: 70, size: 9, fonts, color: pal.muted });
  if (hasField(pageDef, "name")) drawRuns(page, p.name || "", { x: PAGE_W - MARGIN, y: 70, size: 12, fonts, bold: true, color: pal.text, align: "right", maxWidth: 0 });
  if (hasField(pageDef, "designation")) drawRuns(page, p.designation || "", { x: PAGE_W - MARGIN, y: 56, size: 9, fonts, color: pal.muted, align: "right", maxWidth: 0 });
}

function renderDetails(doc, fonts, pal, charter, pageDef, year) {
  const d = charter.sections[pageDef.section_key] || {};
  const lb = charter.local_body || {};
  const page = doc.addPage([PAGE_W, PAGE_H]);
  pageHeader(page, fonts, pal, lb.local_body_name, pageTitle(pageDef), year);
  const autoRows = [
    ["തദ്ദേശ സ്ഥാപനത്തിന്റെ പേര് · Name", lb.local_body_name],
    ["തരം · Type", lb.local_body_type],
    ["ജില്ല · District", lb.district],
    ["ബ്ലോക്ക് · Block", lb.block_panchayat],
  ];
  const fieldRows = fieldsOf(pageDef).map(f => [f.label, d[f.key]]);
  const rows = [...autoRows, ...fieldRows].filter(r => (r[1] || "").toString().trim());
  let y = PAGE_H - 110;
  const labelW = 220;
  for (const [label, value] of rows) {
    const lines = wrapText(String(value), CONTENT_W - labelW - 24, 10.5, fonts, false);
    const h = Math.max(26, lines.length * 15 + 12);
    page.drawRectangle({ x: MARGIN, y: y - h, width: CONTENT_W, height: h, color: C(pal.background), borderColor: C(pal.border), borderWidth: 0.7 });
    drawRuns(page, label, { x: MARGIN + 10, y: y - 18, size: 9.5, fonts, bold: true, color: pal.secondary });
    lines.forEach((ln, i) => drawRuns(page, ln, { x: MARGIN + labelW, y: y - 17 - i * 15, size: 10.5, fonts, color: pal.text }));
    y -= h + 4;
  }
}

// Repeating table pages — chunk records per page (never squeeze); columns from field defs
async function renderRecordTable(doc, fonts, pal, charter, pageDef, year, perPage) {
  const records = charter.sections[pageDef.section_key] || [];
  if (!Array.isArray(records) || !records.length) return;
  const lb = charter.local_body || {};
  const fields = fieldsOf(pageDef);
  const hasPhotos = pageDef.config?.photo && records.some(r => r.photo);
  const cols = [];
  if (hasPhotos) cols.push({ label: "Photo", w: 52, key: "__photo" });
  for (const f of fields) cols.push({ label: f.label, w: 100, key: f.key });
  const slCol = { label: "S.No", w: 30, key: "__sl" };
  const allCols = [slCol, ...cols];
  const totalW = allCols.reduce((s, c) => s + c.w, 0);
  allCols.forEach(c => { c.w = c.w / totalW * CONTENT_W; });

  for (let start = 0; start < records.length; start += perPage) {
    const chunk = records.slice(start, start + perPage);
    const page = doc.addPage([PAGE_W, PAGE_H]);
    pageHeader(page, fonts, pal, lb.local_body_name, pageTitle(pageDef), year);
    let y = PAGE_H - 100;
    // header labels wrap within their column so long bilingual labels never collide
    const headLines = allCols.map(c => wrapText(c.label, c.w - 8, 9, fonts, true));
    const headH = Math.max(...headLines.map(l => l.length), 1) * 12 + 10;
    let x = MARGIN;
    page.drawRectangle({ x: MARGIN, y: y - headH, width: CONTENT_W, height: headH, color: C(pal.table_header) });
    allCols.forEach((c, ci) => {
      headLines[ci].forEach((ln, li) => drawRuns(page, ln, { x: x + 4, y: y - 13 - li * 12, size: 9, fonts, bold: true, color: pal.white }));
      x += c.w;
    });
    y -= headH;
    for (let i = 0; i < chunk.length; i++) {
      const rec = chunk[i];
      let maxLines = 1;
      for (const c of allCols) {
        if (c.key === "__photo") continue;
        const v = c.key === "__sl" ? String(start + i + 1) : String(rec[c.key] ?? "—");
        maxLines = Math.max(maxLines, wrapText(v, c.w - 8, 9.5, fonts, false).length);
      }
      const rowH = Math.max(hasPhotos ? 46 : 24, maxLines * 13 + 10);
      if (i % 2 === 1) page.drawRectangle({ x: MARGIN, y: y - rowH, width: CONTENT_W, height: rowH, color: C(pal.highlight) });
      page.drawRectangle({ x: MARGIN, y: y - rowH, width: CONTENT_W, height: rowH, borderColor: C(pal.border), borderWidth: 0.6 });
      x = MARGIN;
      for (const c of allCols) {
        if (c.key === "__photo") {
          const img = await embedImg(doc, rec.photo);
          if (img) page.drawImage(img, { x: x + 5, y: y - rowH + 4, width: 34, height: rowH - 8 });
        } else {
          const v = c.key === "__sl" ? String(start + i + 1) : String(rec[c.key] ?? "—");
          const lines = wrapText(v, c.w - 8, 9.5, fonts, false);
          lines.forEach((ln, li) => drawRuns(page, ln, { x: x + 4, y: y - 15 - li * 13, size: 9.5, fonts, color: pal.text }));
        }
        x += c.w;
      }
      y -= rowH;
    }
  }
}

async function renderBlocks(doc, fonts, pal, charter, pageDef, year) {
  const blocks = charter.sections[pageDef.section_key] || [];
  if (!Array.isArray(blocks) || !blocks.length) return;
  const lb = charter.local_body || {};
  const title = pageTitle(pageDef);
  let page = doc.addPage([PAGE_W, PAGE_H]);
  pageHeader(page, fonts, pal, lb.local_body_name, title, year);
  let y = PAGE_H - 100;
  const newPage = () => {
    page = doc.addPage([PAGE_W, PAGE_H]);
    pageHeader(page, fonts, pal, lb.local_body_name, title, year);
    y = PAGE_H - 100;
  };
  for (const b of blocks) {
    if (b.type === "heading") {
      const h = 26;
      if (y - h < 70) newPage();
      drawRuns(page, b.text || "", { x: MARGIN, y: y - 18, size: 15, fonts, bold: true, color: pal.primary, maxWidth: CONTENT_W });
      page.drawRectangle({ x: MARGIN, y: y - 24, width: 44, height: 2, color: C(pal.accent) });
      y -= h + 8;
    } else if (b.type === "paragraph") {
      const size = 10.5, lh = 1.55;
      const lines = wrapText(b.text || "", CONTENT_W, size, fonts, false);
      const h = lines.length * size * lh;
      if (y - h < 70) newPage();
      drawParagraph(page, b.text || "", { x: MARGIN, y, size, fonts, color: pal.text, maxWidth: CONTENT_W, lineHeight: lh });
      y -= h + 12;
    } else if (b.type === "image") {
      const img = await embedImg(doc, b.image);
      if (img) {
        const scale = Math.min(CONTENT_W / img.width, 220 / img.height, 1);
        const w = img.width * scale, h = img.height * scale;
        if (y - h < 70) newPage();
        page.drawImage(img, { x: MARGIN + (CONTENT_W - w) / 2, y: y - h, width: w, height: h });
        y -= h + 14;
      }
    } else if (b.type === "contact") {
      const lines = [b.text || ""].filter(Boolean);
      const h = 30 + lines.length * 14;
      if (y - h < 70) newPage();
      page.drawRectangle({ x: MARGIN, y: y - h, width: CONTENT_W, height: h, color: C(pal.background), borderColor: C(pal.border), borderWidth: 0.7 });
      drawRuns(page, "ബന്ധപ്പെടുക · Contact", { x: MARGIN + 10, y: y - 16, size: 9.5, fonts, bold: true, color: pal.secondary });
      lines.forEach((ln, i) => drawRuns(page, ln, { x: MARGIN + 10, y: y - 32 - i * 14, size: 10, fonts, color: pal.text }));
      y -= h + 12;
    } else if (b.type === "divider") {
      page.drawRectangle({ x: MARGIN, y: y - 6, width: CONTENT_W, height: 0.8, color: C(pal.border) });
      y -= 18;
    } else if (b.type === "signature") {
      const img = await embedImg(doc, b.image);
      const h = 60;
      if (y - h < 70) newPage();
      if (img) page.drawImage(img, { x: PAGE_W - MARGIN - 120, y: y - h + 18, width: 120, height: 36 });
      drawRuns(page, b.text || "", { x: PAGE_W - MARGIN, y: y - h + 4, size: 10, fonts, bold: true, color: pal.text, align: "right", maxWidth: 0 });
      y -= h + 10;
    }
  }
}

async function renderBackCover(doc, fonts, pal, charter, pageDef, year) {
  const b = charter.sections[pageDef.section_key] || {};
  const lb = charter.local_body || {};
  const page = doc.addPage([PAGE_W, PAGE_H]);
  page.__cc_plain = true;
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: PAGE_H, color: C(pal.background) });
  page.drawRectangle({ x: 0, y: PAGE_H - 14, width: PAGE_W, height: 14, color: C(pal.primary) });
  page.drawRectangle({ x: 0, y: PAGE_H - 18, width: PAGE_W, height: 4, color: C(pal.accent) });
  let y = PAGE_H - 120;
  if (hasField(pageDef, "image")) {
    const img = await embedImg(doc, b.image);
    if (img) {
      const w = 120, h = 120;
      page.drawImage(img, { x: (PAGE_W - w) / 2, y: y - h, width: w, height: h });
      y -= h + 30;
    }
  }
  drawRuns(page, lb.local_body_name || "", { x: MARGIN, y, size: 22, fonts, bold: true, color: pal.primary, align: "center", maxWidth: CONTENT_W });
  y -= 26;
  drawRuns(page, `പൗരാവകാശ രേഖ ${year} · Citizen Charter`, { x: MARGIN, y, size: 12, fonts, color: pal.heading, align: "center", maxWidth: CONTENT_W });
  y -= 40;
  for (const f of fieldsOf(pageDef)) {
    if (f.key === "image") continue;
    const line = b[f.key];
    if (!(line || "").trim()) continue;
    const used = drawParagraph(page, line, { x: MARGIN, y, size: 11, fonts, color: pal.text, align: "center", maxWidth: CONTENT_W, lineHeight: 1.5 });
    y -= used + 6;
  }
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: 40, color: C(pal.secondary) });
  drawRuns(page, `${lb.local_body_name || ""} · ${lb.district || ""} · Kerala`, { x: MARGIN, y: 16, size: 10, fonts, bold: true, color: pal.white, align: "center", maxWidth: CONTENT_W });
}

/* ------------------------------ main generator ------------------------------
 * charter: the member's charter (sections data, local_body, palette snapshot)
 * template: the active studio template (ordered pages, fonts, design)
 * fixedSets: { [fixed_set_id]: pdf_base64 } — pre-fetched by the caller
 */
export async function generateCharterPdf(charter, { template, fixedSets = {} } = {}) {
  const pages = (template?.pages || []).filter(p => p.enabled !== false);
  const year = template?.year || charter.year || 2026;
  const pal = buildPal(charter.palette || template?.palette);
  const fontFamily = charter.sections?.design?.font_family || template?.fonts?.family || "Anek Malayalam";
  const perPage = Math.max(4, Math.min(12, parseInt(template?.design?.records_per_page) || 8));

  const doc = await PDFDocument.create();
  const vendor = await loadVendor();
  doc.registerFontkit(vendor.fontkit);
  doc.setTitle(`Citizen Charter ${year} — ${charter.local_body?.local_body_name || ""}`);
  doc.setAuthor(charter.local_body?.local_body_name || "Local Body");
  doc.setSubject("Citizen Charter – LSGD");
  doc.setKeywords(["citizen charter", "lsgd", String(year)]);
  doc.setProducer("Citizen Charter Studio");
  doc.setCreator("Citizen Charter Studio");
  const fonts = await buildFonts(doc, fontFamily, vendor);
  const lb = charter.local_body || {};

  for (const pageDef of pages) {
    switch (pageDef.kind) {
      case "cover": renderCover(doc, fonts, pal, charter, pageDef, year); break;
      case "note": await renderNote(doc, fonts, pal, charter, pageDef, year); break;
      case "details": renderDetails(doc, fonts, pal, charter, pageDef, year); break;
      case "record_table": await renderRecordTable(doc, fonts, pal, charter, pageDef, year, perPage); break;
      case "blocks": await renderBlocks(doc, fonts, pal, charter, pageDef, year); break;
      case "back_cover": await renderBackCover(doc, fonts, pal, charter, pageDef, year); break;
      case "fixed_set": {
        const b64 = fixedSets[pageDef.config?.fixed_set_id];
        if (!b64) break;
        try {
          const fixed = await PDFDocument.load(b64, { ignoreEncryption: true });
          const copied = await doc.copyPages(fixed, fixed.getPageIndices());
          copied.forEach(p => { const added = doc.addPage(p); added.__cc_fixed = true; });
        } catch (e) {
          console.error("Fixed set merge failed:", pageDef.config?.fixed_set_id, e);
        }
        break;
      }
      default: break;
    }
  }

  // Page numbers — final assembled order, dynamic pages only (fixed pages untouched)
  const total = doc.getPageCount();
  doc.getPages().forEach((page, idx) => {
    if (page.__cc_fixed || page.__cc_plain) return;
    pageFooter(page, fonts, pal, idx + 1, total, lb.local_body_name);
  });
  return { doc, pageCount: total };
}

export async function charterPdfBlobUrl(charter, opts) {
  const { doc } = await generateCharterPdf(charter, opts);
  const bytes = await doc.save();
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

// Single generation pass returning everything callers need. Blob URLs are used
// for browser preview/download (large data: URLs are unreliable in some browsers).
export async function charterPdfArtifacts(charter, opts) {
  const { doc, pageCount } = await generateCharterPdf(charter, opts);
  const bytes = await doc.save();
  const blobUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return { blobUrl, base64: "data:application/pdf;base64," + btoa(bin), pageCount };
}

export function charterFilename(charter) {
  const lb = (charter.local_body?.local_body_name || "Local_Body").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
  return `Citizen_Charter_${charter.year || 2026}_${lb}.pdf`;
}
